import { ArrowRight, Store, Wand2 } from "lucide-react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button } from "@/shared/ui/core";
import { ClosingCard } from "./ClosingCard";

/**
 * Los dos llamados a la acción del final de la rejilla.
 *
 * Son los dos que pide el documento, con sus palabras:
 *
 *   1. «¿No encuentras el paquete ideal? ¡Crea el tuyo!» → al creador de
 *      paquetes.
 *   2. «¿Buscas producto al por mayor o quieres iniciar tu propio negocio
 *      digital? Adquiere una franquicia digital» → a `/franquicias`.
 *
 * ── El primero apunta a algo que NO EXISTE ───────────────────────────
 * No hay creador de paquetes en este sistema: ni ruta, ni pantalla, ni
 * endpoint. Así que el botón va DESHABILITADO y con la razón escrita
 * debajo, en vez de enlazar a una ruta inventada que caería en el 404 —o
 * peor, a `/shop` disfrazado de creador, que es prometer una cosa y dar
 * otra.
 *
 * La nota al pie sí ofrece la salida honesta que hay hoy: armar el pedido
 * a mano en la tienda. Es menos de lo prometido, y lo dice.
 *
 * Cuando el creador exista, este archivo cambia en una línea: quitar
 * `disabled` y poner `as={Link} to="…"`.
 *
 * ── El segundo sí lleva a una ruta real ──────────────────────────────
 * `/franquicias` está registrada en `app/router.tsx` y es la landing de
 * conversión. Por eso esta es la tarjeta oscura de las dos: es la que el
 * negocio quiere que se pulse.
 */
export function PackagesClosing() {
  const { t } = useTranslation();

  return (
    <div className="grid gap-6 md:grid-cols-2">
      <ClosingCard
        icon={Wand2}
        title={t("¿No encuentras el paquete ideal?")}
        description={t(
          "Podrás armar el tuyo eligiendo productos y cantidades, con el mismo descuento y el mismo cobro mensual.",
        )}
        action={
          /*
            `disabled` sobre un `<button>` de verdad: `Button` le pone
            `disabled` nativo, así que no recibe foco ni clic y el lector
            de pantalla lo anuncia como no disponible. Un enlace "muerto"
            a `#` habría hecho las tres cosas mal.
          */
          <Button variant="secondary" size="lg" disabled>
            {t("¡Crea el tuyo!")}
          </Button>
        }
        footnote={
          <>
            {t("Estamos construyendo el creador de paquetes.")}{" "}
            {t("Mientras tanto puedes armar tu pedido en")}{" "}
            <Link
              to="/shop"
              className="font-semibold text-ds-violet-pop underline decoration-ds-violet-pop/30 underline-offset-2 hover:decoration-ds-violet-pop focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ds-violet-pop/50 focus-visible:ring-offset-2"
            >
              {t("la tienda")}
            </Link>
            .
          </>
        }
      />

      <ClosingCard
        tone="ink"
        icon={Store}
        title={t("¿Buscas producto al por mayor o quieres iniciar tu propio negocio digital?")}
        description={t(
          "La franquicia digital te da tu tienda, el acceso a los paquetes mayoristas y las comisiones de tu red.",
        )}
        action={
          <Button
            as={Link}
            to="/franquicias"
            variant="inverse"
            size="lg"
            rightIcon={<ArrowRight aria-hidden="true" />}
          >
            {t("Adquiere una franquicia digital")}
          </Button>
        }
      />
    </div>
  );
}
